import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ChatWindow } from './ChatWindow';
import { ChatListDropdown } from './ChatListDropdown';
import { MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@/api/messaging';
import { broadcastsApi } from '@/api/broadcasts';
import { supabase } from '@/integrations/supabase/client';
import { getUserEntityId } from '@/lib/chat-utils';

interface ChatWindow {
  id: string;
  name: string;
  avatar?: string;
  role: 'client' | 'filmmaker';
  isMinimized: boolean;
  unreadCount?: number;
}

interface ChatPopupContextType {
  openChats: ChatWindow[];
  openChat: (chat: ChatWindow) => void;
  closeChat: (chatId: string) => void;
  minimizeChat: (chatId: string) => void;
  maximizeChat: (chatId: string) => void;
  isChatListOpen: boolean;
  toggleChatList: () => void;
  closeChatList: () => void;
  totalUnreadCount: number;
  currentUserRole: 'client' | 'filmmaker' | 'admin' | null;
  setCurrentUserRole: (role: 'client' | 'filmmaker' | 'admin') => void;
}

export const ChatPopupContext = createContext<ChatPopupContextType | undefined>(undefined);

export const useChatPopup = () => {
  const context = useContext(ChatPopupContext);
  if (!context) {
    throw new Error('useChatPopup must be used within ChatPopupProvider');
  }
  return context;
};

export const ChatPopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Separate states for filmmaker, client, and admin modes
  const [filmmakerChats, setFilmmakerChats] = useState<ChatWindow[]>([]);
  const [clientChats, setClientChats] = useState<ChatWindow[]>([]);
  const [adminChats, setAdminChats] = useState<ChatWindow[]>([]);
  const [filmmakerChatListOpen, setFilmmakerChatListOpen] = useState(false);
  const [clientChatListOpen, setClientChatListOpen] = useState(false);
  const [adminChatListOpen, setAdminChatListOpen] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<'client' | 'filmmaker' | 'admin' | null>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // State for entity ID
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null);

  // Detect role based on current path
  useEffect(() => {
    const path = location.pathname;
    let detectedRole: 'client' | 'filmmaker' | 'admin' | null = null;

    if (path.includes('/client/')) {
      detectedRole = 'client';
    } else if (path.includes('/admin/')) {
      detectedRole = 'admin';
    } else if (path.includes('/filmmaker/') || path.includes('/dashboard')) {
      detectedRole = 'filmmaker';
    }

    // Only update if role has changed
    if (detectedRole && detectedRole !== currentUserRole) {
      setCurrentUserRole(detectedRole);
    }
  }, [location.pathname, currentUserRole]);

  // Get current user entity ID - cached with React Query for instant availability
  const { data: cachedEntityId } = useQuery({
    queryKey: ['entity-id-popup', currentUserRole],
    queryFn: async () => {
      if (!currentUserRole) return null;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      return getUserEntityId(user.id, currentUserRole);
    },
    enabled: !!currentUserRole,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Update state when cached entity ID is available
  useEffect(() => {
    if (cachedEntityId && cachedEntityId !== currentEntityId) {
      setCurrentEntityId(cachedEntityId);
    }
  }, [cachedEntityId]);

  // Fetch conversations and calculate unread count using getConversationsWithState for consistent participant names
  // Admin users use 'client' role for messaging (they have client_id)
  const effectiveRole = currentUserRole === 'admin' ? 'client' : currentUserRole;
  const { data: conversations } = useQuery({
    queryKey: ['conversations', currentEntityId, currentUserRole],
    queryFn: async () => {
      if (!currentEntityId || !effectiveRole) return [];
      return messagingApi.getConversationsWithState(currentEntityId, effectiveRole);
    },
    enabled: !!currentEntityId && !!currentUserRole,
    staleTime: 30 * 1000,
    refetchInterval: 60000,
  });

  // Fetch broadcasts for unread count (non-admin only)
  const { data: broadcasts } = useQuery({
    queryKey: ['user-broadcasts', currentUserRole],
    queryFn: () => broadcastsApi.getMyBroadcasts(currentUserRole || 'filmmaker'),
    enabled: !!currentUserRole && currentUserRole !== 'admin',
    staleTime: 60 * 1000,
  });

  // Calculate total unread count
  const calculateUnreadCount = useCallback(() => {
    let count = 0;
    
    // Add conversation unread counts
    if (conversations) {
      count += conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
    }
    
    // Add broadcast unread count (if not admin)
    if (broadcasts && currentUserRole !== 'admin') {
      // Get viewed broadcast IDs from localStorage
      const viewedBroadcasts = JSON.parse(localStorage.getItem('viewedBroadcasts') || '[]');
      
      // Only count active broadcasts that haven't been viewed
      const unreadBroadcasts = broadcasts.filter(b => 
        b.is_active && !viewedBroadcasts.includes(b.id)
      );
      count += unreadBroadcasts.length;
    }
    
    setTotalUnreadCount(count);
  }, [conversations, broadcasts, currentUserRole]);

  useEffect(() => {
    calculateUnreadCount();
  }, [calculateUnreadCount]);

  // Listen for broadcast viewed events, message read events, and storage changes
  useEffect(() => {
    const handleBroadcastsViewed = () => {
      // Recalculate unread count when broadcasts are viewed
      calculateUnreadCount();
    };
    
    const handleMessagesRead = () => {
      // Force refetch conversations when messages are read
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // Also recalculate immediately
      setTimeout(() => calculateUnreadCount(), 100);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'viewedBroadcasts') {
        // Recalculate unread count when viewed broadcasts change
        calculateUnreadCount();
      }
    };

    window.addEventListener('broadcasts-viewed', handleBroadcastsViewed);
    window.addEventListener('messages-read', handleMessagesRead);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('broadcasts-viewed', handleBroadcastsViewed);
      window.removeEventListener('messages-read', handleMessagesRead);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [calculateUnreadCount, queryClient]);
  
  // Get the current state based on role
  const openChats = currentUserRole === 'client' ? clientChats : 
                   currentUserRole === 'admin' ? adminChats : filmmakerChats;
  const setOpenChats = currentUserRole === 'client' ? setClientChats : 
                      currentUserRole === 'admin' ? setAdminChats : setFilmmakerChats;
  const isChatListOpen = currentUserRole === 'client' ? clientChatListOpen : 
                        currentUserRole === 'admin' ? adminChatListOpen : filmmakerChatListOpen;
  const setIsChatListOpen = currentUserRole === 'client' ? setClientChatListOpen : 
                           currentUserRole === 'admin' ? setAdminChatListOpen : setFilmmakerChatListOpen;

  // Calculate maximum number of chat windows based on screen size
  const getMaxChats = () => {
    if (isMobile) return 1; // Only one chat at a time on mobile
    if (isTablet) return 2; // Two chats on tablet
    return 3; // Three chats on desktop
  };

  const openChat = useCallback((chat: ChatWindow) => {
    if (!currentUserRole) return; // Don't open chat if role not detected
    
    const setter = currentUserRole === 'client' ? setClientChats : 
                  currentUserRole === 'admin' ? setAdminChats : setFilmmakerChats;
    
    setter((prev) => {
      // Check if chat is already open
      const existingIndex = prev.findIndex((c) => c.id === chat.id);
      if (existingIndex !== -1) {
        // If chat exists, maximize it
        const updated = [...prev];
        updated[existingIndex].isMinimized = false;
        return updated;
      }

      const maxChats = getMaxChats();
      // If we're at max capacity, remove the oldest chat
      if (prev.length >= maxChats) {
        return [...prev.slice(1), { ...chat, isMinimized: false }];
      }
      
      return [...prev, { ...chat, isMinimized: false }];
    });
    
    // Close chat list when opening a chat on mobile
    if (isMobile) {
      const listSetter = currentUserRole === 'client' ? setClientChatListOpen : 
                        currentUserRole === 'admin' ? setAdminChatListOpen : setFilmmakerChatListOpen;
      listSetter(false);
    }
  }, [isMobile, currentUserRole]);

  const closeChat = useCallback((chatId: string) => {
    if (!currentUserRole) return;
    
    const setter = currentUserRole === 'client' ? setClientChats : 
                  currentUserRole === 'admin' ? setAdminChats : setFilmmakerChats;
    setter((prev) => prev.filter((chat) => chat.id !== chatId));
  }, [currentUserRole]);

  const minimizeChat = useCallback((chatId: string) => {
    if (!currentUserRole) return;
    
    const setter = currentUserRole === 'client' ? setClientChats : 
                  currentUserRole === 'admin' ? setAdminChats : setFilmmakerChats;
    setter((prev) =>
      prev.map((chat) =>
        chat.id === chatId ? { ...chat, isMinimized: true } : chat
      )
    );
  }, [currentUserRole]);

  const maximizeChat = useCallback((chatId: string) => {
    if (!currentUserRole) return;
    
    const setter = currentUserRole === 'client' ? setClientChats : 
                  currentUserRole === 'admin' ? setAdminChats : setFilmmakerChats;
    setter((prev) =>
      prev.map((chat) =>
        chat.id === chatId ? { ...chat, isMinimized: false } : chat
      )
    );
  }, [currentUserRole]);

  const toggleChatList = useCallback(() => {
    if (!currentUserRole) return;
    
    const setter = currentUserRole === 'client' ? setClientChatListOpen : 
                  currentUserRole === 'admin' ? setAdminChatListOpen : setFilmmakerChatListOpen;
    setter((prev) => !prev);
  }, [currentUserRole]);

  const closeChatList = useCallback(() => {
    if (!currentUserRole) return;
    
    const setter = currentUserRole === 'client' ? setClientChatListOpen : 
                  currentUserRole === 'admin' ? setAdminChatListOpen : setFilmmakerChatListOpen;
    setter(false);
  }, [currentUserRole]);

  // Close chat list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isChatListOpen &&
        !target.closest('.chat-list-dropdown') &&
        !target.closest('.chat-toggle-button') &&
        !target.closest('.new-chat-modal') &&
        !target.closest('[role="dialog"]') // Also check for any dialog
      ) {
        setIsChatListOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isChatListOpen]);

  return (
    <ChatPopupContext.Provider
      value={{
        openChats,
        openChat,
        closeChat,
        minimizeChat,
        maximizeChat,
        isChatListOpen,
        toggleChatList,
        closeChatList,
        totalUnreadCount,
        currentUserRole,
        setCurrentUserRole,
      }}
    >
      {children}
      <ChatPopupContainer />
    </ChatPopupContext.Provider>
  );
};

const ChatPopupContainer: React.FC = () => {
  const { openChats, closeChat, isChatListOpen, toggleChatList, totalUnreadCount } = useChatPopup();
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <>

      {/* Chat List Dropdown */}
      <AnimatePresence>
        {isChatListOpen && (
          <>
            {/* Backdrop for mobile - only show if no chat windows are open or not transparent */}
            {isMobile && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={toggleChatList}
              />
            )}
            <motion.div
              initial={
                isMobile 
                  ? { y: "100%" }
                  : { opacity: 0, y: -10 }
              }
              animate={
                isMobile 
                  ? { y: 0 }
                  : { opacity: 1, y: 0 }
              }
              exit={
                isMobile 
                  ? { y: "100%" }
                  : { opacity: 0, scale: 0.95, y: -10 }
              }
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300
              }}
              className={cn(
                "fixed z-50 chat-list-dropdown",
                isMobile
                  ? "bottom-0 left-0 right-0 h-[70vh] bg-background rounded-t-2xl shadow-2xl" // Bottom sheet on mobile
                  : "top-20 right-6 w-[400px] h-[600px] bg-background border rounded-lg shadow-lg overflow-hidden"
              )}
            >
              <ChatListDropdown />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Open Chat Windows - Desktop only */}
      {!isMobile && (
        <div className="fixed bottom-0 right-0 flex items-end gap-2 p-4 z-40">
          <AnimatePresence>
            {openChats.map((chat, index) => (
              <ChatWindow
                key={chat.id}
                chat={chat}
                position={index}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Mobile Chat Window - Bottom sheet style */}
      <AnimatePresence>
        {isMobile && openChats.length > 0 && (
          <ChatWindow
            key={openChats[openChats.length - 1].id}
            chat={openChats[openChats.length - 1]}
            position={0}
            isMobile={true}
          />
        )}
      </AnimatePresence>
    </>
  );
};